/* eslint-disable */
import { Tween } from './Tween';
import { Easing } from './easing';
import type { EasingType } from './easing';
import { Container, Ticker } from 'pixi.js';

export class TweenManager {
  public currentTime = 0;

  public easing = Easing;

  private tweens: Tween[] = [];

  private tweensToAdd: Tween[] = [];

  private timeScale = 1;

  constructor(autoUpdate: boolean = false) {
    if (autoUpdate) {
      Ticker.shared.add(this.autoUpdate);
    }
  }

  public setTimeScale(scale: number) {
    this.timeScale = scale;
  }

  public add(tween: Tween): void {
    // tween._manager = this;
    this.tweensToAdd.push(tween);
  }

  public create(target: object): Tween {
    return new Tween(target, this);
  }

  public createValueTween(
    fromValue: number,
    targetValue: number,
    duration: number,
    callback: (value: number) => unknown,
    easing: EasingType = 'linear',
  ): Tween {
    const targetObj = { a: fromValue };
    const tween = new Tween(targetObj, this);
    tween.to({ a: targetValue }, duration, easing, true).onUpdateCallback(() => {
      callback(targetObj.a);
    });
    return tween;
  }

  public isTweenActive(tween: Tween): boolean {
    return !tween.pendingDelete && (this.tweens.includes(tween) || this.tweensToAdd.includes(tween));
  }

  public remove(tween: Tween): void {
    const i: number = this.tweens.indexOf(tween);

    tween.onRemoved.dispatch();

    if (i !== -1) {
      this.tweens[i].pendingDelete = true;
    } else {
      const iToAdd: number = this.tweensToAdd.indexOf(tween);
      if (iToAdd !== -1) {
        this.tweensToAdd[iToAdd].pendingDelete = true;
      }
    }
  }

  private autoUpdate = () => {
    this.update(Ticker.shared.elapsedMS);
  };

  public update(deltaTime: number): boolean {
    this.currentTime += deltaTime * this.timeScale;

    const addTweensLen: number = this.tweensToAdd.length;
    let tweensLen: number = this.tweens.length;

    if (tweensLen === 0 && addTweensLen === 0) {
      return false;
    }

    let i = 0;

    while (i < tweensLen) {
      if (this.tweens[i].update(this.currentTime, deltaTime * this.timeScale)) {
        i++;
      } else {
        this.tweens.splice(i, 1);
        tweensLen--;
      }
    }

    if (addTweensLen > 0) {
      this.tweens = this.tweens.concat(this.tweensToAdd);
      this.tweensToAdd.length = 0;
    }

    return true;
  }

  public getAll(): Tween[] {
    return this.tweens;
  }

  public removeAll(): void {
    for (const tween of this.tweens) {
      tween.pendingDelete = true;
    }
    this.tweensToAdd = [];
  }

  public removeTweensOf(target: object | object[] | Container, recursive: boolean = false): void {
    this.getTweensOf(target, recursive).forEach((tween) => {
      this.remove(tween);
    });
  }

  public isTweening(target: object): boolean {
    return this.tweens.some((tween: Tween) => tween.target === target);
  }

  public pauseAll(): void {
    for (const tween of this.tweens) {
      tween.pause();
    }
  }

  public resumeAll(): void {
    for (const tween of this.tweens) {
      tween.resume();
    }
  }

  public pauseTweensOf(target: Container, recursive: boolean = false): void {
    this.getTweensOf(target, recursive).forEach((tween) => {
      tween.pause();
    });
  }

  public resumeTweensOf(target: Container, recursive: boolean = false): void {
    this.getTweensOf(target, recursive).forEach((tween) => {
      tween.resume();
    });
  }

  private getTweensOf(target: object | object[] | Container, recursive: boolean): Tween[] {
    const tweens: Tween[] = [];

    if (Array.isArray(target)) {
      target.forEach((t: object) => tweens.push(...this.getTweensOf(t, recursive)));
      return tweens;
    }

    tweens.push(...this.tweens.filter((tween) => tween.target === target));

    if (recursive && target instanceof Container) {
      for (let i = 0; i < target.children.length; i++) {
        tweens.push(...this.getTweensOf(target.children[i], true));
      }
    }
    return tweens;
  }

  public rewindTween(tween: Tween, delta: number): void {
    tween.update(this.currentTime, delta);
  }

  public destroy() {
    Ticker.shared.remove(this.autoUpdate);
  }
}
